/* Лист «Права на материалы» для движка шаблона (engine-kit, план движков §5.5 и §6 «Consent and upload»): BotCore.sheet с id ekRights,
   разметка вставляется в #bot при первом открытии (как lazySheet BotCore). Внутри — шапка листа, строка-переключатель прав
   (BotCore.switchRow, как StudioUploadRights) и «Продолжить загрузку»: неактивна (BotCore.offBtn — дрожь и причина), пока права не отмечены
   или сервис заперт. Переключатель пишет права в сервис заказа (setRights); «Продолжить загрузку» закрывает лист без отмены и зовёт
   onContinue (мост снова выбирает тот же файл); любое другое закрытие — крестик, подложка, Escape, «Назад» Telegram, close() — onClose
   (файл не грузится). Одновременно открыт один такой лист: новое открытие отменяет прежнее.
   M3 (план §2 S2, необязательные параметры — без них лист прежний байт в байт): texts — свои тексты листа (кавер «Права на песню»);
   confirm — подтверждение без файла (права после перезагрузки: кнопка «Подтвердить»); field — поле формы, которое переключатель пишет
   вместе с правами (consent_accepted «Повтори движения», rights_confirmed кавера), consentField() — его запись по кодированию формы. */
import { uiText } from '../../../i18n';
import { studioFields } from '../studio-api';
import type { OrderService } from './order-service';

interface CoreSheet {
  html(): string;
  open(html: string, opt?: Record<string, unknown>): void;
  paint(html: string): void;
  close(): void;
  readonly isOpen: boolean;
}
interface Core {
  sheet(o: { id: string; label: string; onClose(): void }): CoreSheet;
  shead(o: Record<string, unknown>): string;
  switchRow(o: { attr: string; on: boolean; icon: string; title: string; sub: string }): string;
  switchSet(el: Element, on: boolean): void;
  offBtn(label: string, miss: string, cls?: string): string;
  isOff(el: Element): boolean;
  shake(el: Element, buzz?: unknown): void;
  offBuzz?: unknown;
  ic(name: string): string;
  esc(value: string): string;
}
/** Свои тексты листа (русские строки; перевод — uiText при показе) */
export interface ConsentTexts {
  /** Заголовок листа и подпись панели */
  title?: string;
  /** Строка переключателя: заголовок и подпись под ним */
  row?: string;
  sub?: string;
  /** Кнопка внизу */
  button?: string;
  /** Подсказка неактивной кнопки, пока переключатель не отмечен */
  miss?: string;
}
export type ConsentField = 'consent_accepted' | 'rights_confirmed';
export interface ConsentOptions {
  rights(): boolean;
  locked(): boolean;
  setRights(on: boolean): void;
  onContinue(): void;
  onClose(): void;
  texts?: ConsentTexts;
  /** Подтверждение без файла: кнопка «Подтвердить», onContinue — права подтверждены */
  confirm?: boolean;
  /** Поле формы, которое переключатель пишет вместе с правами */
  field?: ConsentField;
  /** Значение поля сейчас; без него поле считается отмеченным вместе с правами */
  fieldOn?(): boolean;
  /** Записать поле (экран кодирует значение по форме) */
  setField?(on: boolean): void;
}
export interface ConsentSheet {
  close(): void;
  isOpen(): boolean;
  /** Перерисовать кнопку по правам и замку сервиса (изменились не из листа) */
  refresh(): void;
}

export const CONSENT_ID = 'ekRights';
const core = () => (window as unknown as { BotCore: Core }).BotCore;
/** Лист BotCore на объект BotCore (тесты исполняют наследие заново — у нового BotCore свой реестр листов) */
let made: { bc: Core; sheet: CoreSheet } | null = null;
let current: { o: ConsentOptions; going: boolean } | null = null;

const titleOf = (o: ConsentOptions) => uiText(o.texts?.title ?? 'Права на материалы');
/** Переключатель отмечен: права и (если лист пишет поле) поле формы */
const switchOn = (o: ConsentOptions) => o.rights() && (!o.field || (o.fieldOn ? o.fieldOn() : true));

function closed() {
  const c = current;
  current = null;
  document.removeEventListener('click', onClick);
  if (c && !c.going) c.o.onClose();
}
function sheetOf(bc: Core): CoreSheet {
  if (made?.bc !== bc) made = { bc, sheet: bc.sheet({ id: CONSENT_ID, label: uiText('Права на материалы'), onClose: closed }) };
  if (!document.getElementById(CONSENT_ID)) (document.getElementById('bot') ?? document.body).insertAdjacentHTML('beforeend', made.sheet.html());
  return made.sheet;
}
function ctaHTML(bc: Core, o: ConsentOptions) {
  const label = uiText(o.texts?.button ?? (o.confirm ? 'Подтвердить' : 'Продолжить загрузку'));
  if (!switchOn(o)) return bc.offBtn(label, uiText(o.texts?.miss ?? 'Подтвердите права на материалы'), 'fin');
  if (o.locked()) return bc.offBtn(label, uiText('Проверяем доступность'), 'fin');
  return `<button class="btn fin" type="button" data-ek-go>${bc.esc(label)}</button>`;
}
function bodyHTML(bc: Core, o: ConsentOptions) {
  return (
    `<div class="cbody ek-rights">${bc.shead({ title: titleOf(o), x: 'data-bsheet-x', xLabel: uiText('Закрыть') })}` +
    bc.switchRow({
      attr: 'data-ek-rights',
      on: switchOn(o),
      /* у BotCore нет значка photo (пустой кружок): фото шаблона — image, как «Мои фото» bot-people.js */
      icon: bc.ic('image'),
      title: bc.esc(uiText(o.texts?.row ?? 'Права на материалы')),
      sub: bc.esc(uiText(o.texts?.sub ?? 'Только свои файлы или с согласия владельца')),
    }) +
    `<div class="cta" id="${CONSENT_ID}Cta">${ctaHTML(bc, o)}</div></div>`
  );
}
function paintCta() {
  const c = current,
    el = document.getElementById(`${CONSENT_ID}Cta`);
  if (c && el) el.innerHTML = ctaHTML(core(), c.o);
}
function onClick(event: MouseEvent) {
  const c = current;
  const el = event.target instanceof Element ? event.target.closest('[data-ek-rights],[data-ek-go]') : null;
  if (!c || !el || !el.closest(`#${CONSENT_ID}`)) return;
  const bc = core();
  if (el.hasAttribute('data-ek-rights')) {
    if (c.o.locked()) {
      bc.shake(el, bc.offBuzz);
      return;
    }
    const on = !switchOn(c.o);
    c.o.setRights(on);
    if (c.o.field) c.o.setField?.(on);
    bc.switchSet(el, switchOn(c.o));
    paintCta();
    return;
  }
  /* неактивную кнопку (data-bc-off) объясняет сам BotCore */
  if (bc.isOff(el) || !switchOn(c.o) || c.o.locked()) return;
  c.going = true;
  made?.sheet.close();
  c.o.onContinue();
}

export function openConsentSheet(o: ConsentOptions): ConsentSheet {
  const bc = core();
  if (current) {
    /* другой файл ждал согласия — он отменяется */
    const previous = current;
    current = null;
    previous.o.onClose();
  }
  const sheet = sheetOf(bc);
  const mine = { o, going: false };
  current = mine;
  document.addEventListener('click', onClick);
  sheet.open(bodyHTML(bc, o));
  /* подпись панели — по тексту этого открытия и языку сейчас (лист создаётся один раз на BotCore) */
  document.getElementById(CONSENT_ID)?.setAttribute('aria-label', titleOf(o));
  return {
    close() {
      if (current === mine) sheet.close();
    },
    isOpen: () => current === mine && sheet.isOpen,
    refresh() {
      if (current === mine) paintCta();
    },
  };
}

/** Поле согласия формы для листа (field, fieldOn, setField): значение кодируется по полю — JSON (rights_confirmed кавера:
    true или ключа нет, как patchSpec), иначе строка 'true' / 'false' (consent_accepted «Повтори движения», как MotionConsent).
    Форма поля не публикует — null (лист пишет только права). */
export function consentField(svc: OrderService, key: ConsentField): Pick<ConsentOptions, 'field' | 'fieldOn' | 'setField'> | null {
  const field = studioFields(svc.get().form).find(item => item.key === key);
  if (!field) return null;
  const json = field.value_encoding === 'json';
  return {
    field: key,
    fieldOn: () => svc.get().inputs[key] === 'true',
    setField: on =>
      svc.setters.inputs(current => {
        if (on ? current[key] === 'true' : json ? !(key in current) : current[key] === 'false') return current;
        const next = { ...current };
        if (on) next[key] = 'true';
        else if (json) delete next[key];
        else next[key] = 'false';
        return next;
      }),
  };
}
